import {
  SAVE_PROGRESS_ROUNDS_MAX,
  SAVE_PROGRESS_ROUNDS_MAX_BODY_BYTES,
} from '@mfw/common';
import { omitDeepUndefined } from '@mfw/components';

import type {
  UpdateWorkoutMarketplaceConfigArgs,
  UpdateWorkoutProgressByIdArgs,
  SaveWorkoutProgressRoundsArgs,
  CreateWorkoutProgressArgs,
  RoundFieldValue,
  CreateWorkoutFeedbackArgs,
  FetchWorkoutFeedbackArgs,
  FetchWorkoutResultsArgs,
  UnarchiveWorkoutByIdArgs,
  FetchSignedUploadUrlArgs,
  ArchiveWorkoutByIdArgs,
  ApiWorkoutFeedback,
  ApiWorkoutExerciseFeedbackRow,
  FetchWorkoutsArgs,
  CreateWorkoutArgs,
  FetchWorkoutById,
  EditWorkoutArgs,
} from './types/workouts.types';
import type {
  CreateWorkoutFeedbackCommentArgs,
  FetchWorkoutFeedbackCommentsArgs,
  ApiWorkoutFeedbackComment,
} from './types/workoutFeedbackComments.types';
import type {
  ResetWorkoutProgressByWorkoutIdArgs,
  WorkoutProgress,
} from './types/progress.types';
import type {
  ApiSignedUploadUrl,
  UiSignedUploadUrl,
} from './types/files.types';
import type { ApiWorkout } from '../schemas/workouts.schema';
import type { BaseApiResponse } from './types/api.types';

import { mapWorkoutFeedbackCommentFromApi } from './mappers/workoutFeedbackComments.mappers';
import {
  mapWorkoutFeedbackFromApi,
  createWorkoutApiArgs,
  mapWorkoutFromApi,
} from './mappers/workouts.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';
import { FormatDate } from '../utils';

export const fetchSignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchSignedUploadUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export const createWorkout = async ({
  organizationId,
  ...args
}: CreateWorkoutArgs) => {
  const response = await post<ApiWorkout>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts`,
    createWorkoutApiArgs(args),
  );
  if (!response?.data) return null;
  return mapWorkoutFromApi(response?.data, args.locale, args.userUnitSettings);
};

export const editWorkoutById = async ({
  id,
  organizationId,
  ...args
}: EditWorkoutArgs) => {
  const response = await patch<ApiWorkout>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts/${id}`,
    createWorkoutApiArgs(args),
  );
  if (!response?.data) return null;
  return mapWorkoutFromApi(response?.data, args.locale, args.userUnitSettings);
};

export const fetchWorkoutById = async ({
  id,
  locale,
  organizationId,
  userUnitSettings,
}: FetchWorkoutById) => {
  const url = `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts/${id}`;
  const response = await get<BaseApiResponse<ApiWorkout>>(url);
  if (!response?.data) return null;
  return mapWorkoutFromApi(response?.data, locale, userUnitSettings);
};

export const fetchWorkouts = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
  userUnitSettings,
}: FetchWorkoutsArgs) => {
  const { search, ...restTableFilters } = tableFilters;
  const {
    authorIds,
    equipmentTags,
    isDeleted,
    isHome,
    isMarketplace,
    organizationIds,
    selectedUserIds,
    sources,
    tags,
  } = contentFilters || {};
  const params = {
    ...restTableFilters,
    status: 'active',
    ...(search ? { search } : {}),
    authorIds,
    equipmentTagIds: equipmentTags || undefined,
    isDeleted,
    isHome,
    isMarketplace,
    organizationIds,
    selectedUserIds,
    sources,
    tagIds: tags || undefined,
  };

  const response = await postWithPagination<ApiWorkout[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts/list`,
    params,
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response?.data.map((workout) =>
      mapWorkoutFromApi(workout, locale, userUnitSettings),
    ),
  };
};

export const updateWorkoutMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateWorkoutMarketplaceConfigArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts/${id}/marketplace-config`,
    {
      configuration: {
        marketplace: { currency, enabled, price },
      },
    },
  );
};

export const archiveWorkoutById = async ({
  id,
  organizationId,
}: ArchiveWorkoutByIdArgs) => {
  await destroy(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts/${id}`,
  );
  return true;
};

export const createWorkoutProgress = async (
  args: CreateWorkoutProgressArgs,
) => {
  await post('/v2/progress', args);
};

/**
 * Narrows one round field to the string the API stores. Every branch ends in a
 * string — a cleared field included — because the backend DTO is `z.string()`
 * and anything else comes back as a 422 the user never sees.
 */
function toWireFeedback(value: RoundFieldValue): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value[0]?.value ?? '';
  if (value instanceof Date) {
    return FormatDate.formatDateTimeToSeconds(value).toString();
  }
  // NUMBER and the three DECIMAL formats arrive here as numbers. This branch
  // used to be missing, which is the whole of MYFITWORLD-V2-6D.
  return String(value);
}

function mapFeedbacksToApi(
  feedbacks: UpdateWorkoutProgressByIdArgs['feedbacks'],
) {
  if (!feedbacks) return undefined;
  return feedbacks.map((entry) => ({
    configurableId: entry.configurableId,
    feedback: toWireFeedback(entry.feedback),
  }));
}
export const updateWorkoutProgressById = async (
  args: UpdateWorkoutProgressByIdArgs,
): Promise<WorkoutProgress | null> => {
  const mappedFeedbacks = mapFeedbacksToApi(args.feedbacks);

  // Build request body conditionally
  const body = mappedFeedbacks ? { ...args, feedbacks: mappedFeedbacks } : args;

  const response = await patch('/v2/progress', body);

  if (!response?.data) return null;

  return response.data as WorkoutProgress;
};

/** UTF-8 bytes of a value's JSON — what the backend's body limit counts. */
const jsonBytes = (value: unknown) => new Blob([JSON.stringify(value)]).size;

/**
 * Every round of a finished session in one request — what the feedback
 * wizards' "Finish" used to send as one `PATCH /v2/progress` per round. A
 * finish that does not fit one request (`SAVE_PROGRESS_ROUNDS_MAX` rounds,
 * `SAVE_PROGRESS_ROUNDS_MAX_BODY_BYTES` of JSON) goes in several, one after
 * another and in order, so no body outgrows what the backend parses. A single
 * round larger than that goes alone.
 */
export const saveWorkoutProgressRounds = async ({
  rounds,
  ...args
}: SaveWorkoutProgressRoundsArgs): Promise<WorkoutProgress | null> => {
  const wireRounds = rounds.map(({ feedbacks, workoutExerciseId }) => {
    const mappedFeedbacks = mapFeedbacksToApi(feedbacks);
    return mappedFeedbacks
      ? { feedbacks: mappedFeedbacks, workoutExerciseId }
      : { workoutExerciseId };
  });

  // The body is `{...args, rounds: [r1,r2,…]}`: its size is the empty
  // envelope plus each round plus one comma between rounds.
  const envelopeBytes = jsonBytes({ ...args, rounds: [] });
  const requests: Array<typeof wireRounds> = [];
  let current: typeof wireRounds = [];
  let currentBytes = envelopeBytes;
  for (const round of wireRounds) {
    const roundBytes = jsonBytes(round);
    const isFull =
      current.length === SAVE_PROGRESS_ROUNDS_MAX ||
      (current.length > 0 &&
        currentBytes + 1 + roundBytes > SAVE_PROGRESS_ROUNDS_MAX_BODY_BYTES);
    if (isFull) {
      requests.push(current);
      current = [];
      currentBytes = envelopeBytes;
    }
    currentBytes += (current.length > 0 ? 1 : 0) + roundBytes;
    current.push(round);
  }
  if (current.length > 0) requests.push(current);

  let response: Awaited<ReturnType<typeof patch>> | undefined;
  for (const requestRounds of requests) {
    response = await patch('/v2/progress/rounds', {
      ...args,
      rounds: requestRounds,
    });
  }

  if (!response?.data) return null;

  return response.data as WorkoutProgress;
};

export const resetWorkoutProgressByWorkoutId = async ({
  scheduleId,
  workoutId,
}: ResetWorkoutProgressByWorkoutIdArgs) => {
  const response = await post(
    `/v2/progress/${workoutId}/workout/reset`,
    scheduleId ? { scheduleId } : undefined,
  );
  if (!response?.data) return null;
  return response?.data as WorkoutProgress;
};

/**
 * Creates feedback for workout and marks it as completed. Returns the
 * created row (its `id` anchors the comment thread).
 */
export const createWorkoutFeedback = async ({
  workoutId,
  organizationId,
  userId,
  ...other
}: CreateWorkoutFeedbackArgs) => {
  const omitEmptyValues = omitDeepUndefined({ ...other });
  const basePath =
    !other.scheduleId && organizationId && userId
      ? `/v2/organizations/${organizationId}/users/${userId}/progress`
      : '/v2/progress';
  const response = await post<ApiWorkoutFeedback>(
    `${basePath}/${workoutId}/workout/feedback`,
    omitEmptyValues,
  );
  if (!response?.data) return null;
  return mapWorkoutFeedbackFromApi(response.data);
};

/**
 * Latest submitted feedback for a workout. With `organizationId`/`userId`,
 * reads a client's feedback (trainer view); without them, reads the
 * caller's own via the plain route (client viewing their own schedule).
 */
export const fetchWorkoutFeedback = async ({
  organizationId,
  workoutId,
  userId,
}: FetchWorkoutFeedbackArgs) => {
  const basePath =
    organizationId && userId
      ? `/v2/organizations/${organizationId}/users/${userId}/progress`
      : '/v2/progress';
  const response = await get<BaseApiResponse<ApiWorkoutFeedback | null>>(
    `${basePath}/${workoutId}/workout/feedback`,
  );
  if (!response?.data) return null;
  return mapWorkoutFeedbackFromApi(response.data);
};

/**
 * Achieved round values for a finished workout. Same self/org-scoped split as
 * `fetchWorkoutFeedback` — no assigned values here, the caller already has
 * those from the workout itself.
 */
export const fetchWorkoutResults = async ({
  organizationId,
  workoutId,
  userId,
}: FetchWorkoutResultsArgs) => {
  const basePath =
    organizationId && userId
      ? `/v2/organizations/${organizationId}/users/${userId}/progress`
      : '/v2/progress';
  const response = await get<BaseApiResponse<ApiWorkoutExerciseFeedbackRow[]>>(
    `${basePath}/${workoutId}/workout/results`,
  );
  return response?.data ?? [];
};

const workoutFeedbackCommentsBasePath = (
  organizationId?: string,
  userId?: string,
) => {
  if (!!organizationId !== !!userId) {
    throw new Error('organizationId and userId must be provided together');
  }
  return organizationId && userId
    ? `/v2/organizations/${organizationId}/users/${userId}/progress`
    : '/v2/progress';
};

/**
 * Flat thread, chronological — no org/user-scoped variant on the backend.
 * Anchored on `workoutId` directly (not a specific feedback submission): a
 * workout can carry several completions over time, and this is the one
 * ongoing trainer/client conversation about it, not one thread per
 * completion.
 */
export const fetchWorkoutFeedbackComments = async ({
  organizationId,
  userId,
  workoutId,
}: FetchWorkoutFeedbackCommentsArgs) => {
  const basePath = workoutFeedbackCommentsBasePath(organizationId, userId);
  const response = await get<BaseApiResponse<ApiWorkoutFeedbackComment[]>>(
    `${basePath}/feedback/${workoutId}/comments`,
  );
  return (response?.data ?? []).map(mapWorkoutFeedbackCommentFromApi);
};

export const createWorkoutFeedbackComment = async ({
  organizationId,
  userId,
  workoutId,
  comment,
}: CreateWorkoutFeedbackCommentArgs) => {
  const basePath = workoutFeedbackCommentsBasePath(organizationId, userId);
  const response = await post<ApiWorkoutFeedbackComment>(
    `${basePath}/feedback/${workoutId}/comments`,
    { comment },
  );
  if (!response?.data) return null;
  return mapWorkoutFeedbackCommentFromApi(response.data);
};

export const unarchiveWorkoutById = async ({
  id,
  organizationId,
}: UnarchiveWorkoutByIdArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workouts/${id}/restore`,
  );
  return true;
};
